import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { Journalist } from '../../../types/journalist.types';
import { deleteJournalist, setPage, setRowsPerPage, fetchJournalists, fetchJournalistsByPublisher } from '../slices/journalistsSlice';
import JournalistForm from './JournalistForm';
import ConfirmDialog from './common/ConfirmDialog';

interface JournalistsTableProps {
  publisherId?: string; // Optional - filters journalists by publisher
}

const JournalistsTable: React.FC<JournalistsTableProps> = ({ publisherId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filteredItems, isLoading, pagination } = useSelector((state: RootState) => state.journalists);
  
  const [openForm, setOpenForm] = useState(false);
  const [selectedJournalist, setSelectedJournalist] = useState<Journalist | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [journalistToDelete, setJournalistToDelete] = useState<Journalist | null>(null);

  // Fetch journalists on mount and when publisherId changes
  useEffect(() => {
    if (publisherId) {
      dispatch(fetchJournalistsByPublisher(publisherId));
    } else {
      dispatch(fetchJournalists());
    }
  }, [dispatch, publisherId]);

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setSelectedJournalist(undefined);
    setOpenForm(true);
  };

  const handleOpenEdit = (journalist: Journalist) => {
    setIsEdit(true);
    setSelectedJournalist(journalist);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleDeleteClick = (journalist: Journalist, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setJournalistToDelete(journalist);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (journalistToDelete) {
      try {
        await dispatch(deleteJournalist(journalistToDelete.id));
        setOpenConfirmDelete(false);
        setJournalistToDelete(null);
      } catch (error) {
        console.error('Error deleting journalist:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setJournalistToDelete(null);
  };

  // Handle row click to open editor
  const handleRowClick = (journalist: Journalist) => {
    handleOpenEdit(journalist);
  };

  const items = filteredItems;

  const displayedItems = items.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  );
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Journalists {publisherId ? 'for Selected Publisher' : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={isLoading || !publisherId}
        >
          Add Journalist
        </Button>
      </Box>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Specialties</TableCell>
                <TableCell>Social Links</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No journalists found.
                    </Typography>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleAddClick}
                      sx={{ mt: 1 }}
                    >
                      Add your first journalist
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.map((journalist) => (
                  <TableRow key={journalist.id} hover onClick={() => handleRowClick(journalist)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={journalist.profileImageUrl} 
                          alt={journalist.name}
                          sx={{ width: 40, height: 40, mr: 2 }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {journalist.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {journalist.email || '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {journalist.specialties && journalist.specialties.length > 0 ? (
                          journalist.specialties.map((specialty) => (
                            <Chip 
                              key={specialty} 
                              label={specialty} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No specialties listed
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {journalist.socialLinks?.twitter && (
                          <Tooltip title="Twitter">
                            <IconButton 
                              size="small" 
                              href={journalist.socialLinks.twitter} 
                              target="_blank" 
                              color="primary"
                            >
                              <TwitterIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {journalist.socialLinks?.linkedin && (
                          <Tooltip title="LinkedIn">
                            <IconButton 
                              size="small" 
                              href={journalist.socialLinks.linkedin} 
                              target="_blank" 
                              color="primary"
                            >
                              <LinkedInIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!journalist.socialLinks?.twitter && !journalist.socialLinks?.linkedin && '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={(event) => handleDeleteClick(journalist, event)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={items.length}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Journalist Form Dialog */}
      <JournalistForm
        open={openForm}
        onClose={handleCloseForm}
        journalist={selectedJournalist}
        isEdit={isEdit}
        publisherId={publisherId}
      />
      
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${journalistToDelete?.name}?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default JournalistsTable;